import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2, Mail, ArrowLeft, CheckCircle, Lock, KeyRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import * as api from '@/lib/api';

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('email'); // email | code | success
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleSendCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await api.forgotPassword(email);
      setStep('code');
      toast.success('Código enviado para seu email!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar email');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('A senha deve ter pelo menos 8 caracteres');
      return;
    }
    
    setLoading(true);
    
    try {
      await api.resetPassword(email, code, newPassword);
      setStep('success');
      toast.success('Senha alterada com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alterar senha');
    } finally {
      setLoading(false);
    }
  };

  const handleGoToLogin = () => {
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background flex flex-col overflow-x-hidden">
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          {/* Logo */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-block mb-6">
              <img 
                src="/logo.png" 
                alt="Viral Clipes" 
                className="w-16 h-16 mx-auto"
              />
            </Link>
            
            {step === 'email' && (
              <>
                <h1 className="text-2xl font-heading font-bold mb-2">
                  Esqueceu a senha?
                </h1>
                <p className="text-muted-foreground text-sm">
                  Digite seu email para receber um código de recuperação
                </p>
              </>
            )}
            
            {step === 'code' && (
              <>
                <h1 className="text-2xl font-heading font-bold mb-2">
                  Verificar código
                </h1>
                <p className="text-muted-foreground text-sm">
                  Digite o código de 6 dígitos enviado para {email}
                </p>
              </>
            )}
            
            {step === 'success' && (
              <>
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </div>
                <h1 className="text-2xl font-heading font-bold mb-2">
                  Senha alterada!
                </h1>
                <p className="text-muted-foreground text-sm">
                  Sua senha foi alterada com sucesso. Faça login com sua nova senha.
                </p>
              </>
            )}
          </div>

          {/* Step 1: Email */}
          {step === 'email' && (
            <form onSubmit={handleSendCode} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">
                  Email
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="seu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-12 text-base bg-muted/30 border-border/60 focus:border-primary"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full h-12 text-base font-medium"
                disabled={loading}
                data-testid="send-code-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  'Enviar código de recuperação'
                )}
              </Button>
            </form>
          )}

          {/* Step 2: Code + New Password */}
          {step === 'code' && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="code" className="text-sm font-medium">
                  Código de verificação
                </Label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="code"
                    type="text"
                    placeholder="000000"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="pl-10 h-12 text-base bg-muted/30 border-border/60 focus:border-primary tracking-widest text-center font-mono"
                    required
                    maxLength={6}
                    data-testid="code-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="newPassword" className="text-sm font-medium">
                  Nova senha
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="newPassword"
                    type="password"
                    placeholder="Mínimo 8 caracteres"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="pl-10 h-12 text-base bg-muted/30 border-border/60 focus:border-primary"
                    required
                    minLength={8}
                    data-testid="new-password-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-sm font-medium">
                  Confirmar nova senha
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="Repita a senha"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 h-12 text-base bg-muted/30 border-border/60 focus:border-primary"
                    required
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full h-12 text-base font-medium"
                disabled={loading || code.length !== 6}
                data-testid="reset-password-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  'Alterar senha'
                )}
              </Button>

              <button
                type="button"
                onClick={() => setStep('email')}
                className="w-full text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                Não recebeu o código? Reenviar
              </button>
            </form>
          )}

          {/* Step 3: Success */}
          {step === 'success' && (
            <Button 
              onClick={handleGoToLogin}
              className="w-full h-12 text-base font-medium"
              data-testid="go-to-login-btn"
            >
              Ir para o login
            </Button>
          )}

          {/* Back to Login */}
          {step !== 'success' && (
            <Link 
              to="/login" 
              className="flex items-center justify-center gap-2 mt-8 text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar para o login
            </Link>
          )}
        </div>
      </main>
    </div>
  );
}
